import { ArrowRight } from "lucide-react";
import { MANROPE, toneOf, BAND_INK, LABEL } from "./overviewTokens";

const Tile = ({ t, first }) => (
  <div className="px-5 py-4" style={{ borderLeft: first ? "none" : "1px solid #2A2A2A" }}>
    <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#8A857C]">{t.label}</div>
    <div className="mt-1.5 text-[38px] font-extrabold leading-[1.05] whitespace-nowrap"
         style={{ ...MANROPE, color: BAND_INK[t.tone] || "#FFFFFF" }}>{t.value}</div>
    <div className="mt-1 text-[12px] text-[#B8B4AC]">{t.sub}</div>
  </div>
);

const Watch = ({ items, empty }) => (
  <div className="min-w-0 space-y-1">
    {items.length === 0
      ? <div className="text-[12px] text-[#B5AFA3]">{empty}</div>
      : items.map((it, i) => (
        <div key={i} className="text-[12.5px] leading-[1.35] text-[#1A1A1A]">
          <span className="font-semibold">{it.name}</span>
          {it.owner && <span className="text-[#8A857C]"> · {it.owner}</span>}
        </div>
      ))}
  </div>
);

const Row = ({ a, onOpen }) => {
  const tone = toneOf(a.tone);
  return (
    <div className="grid items-start gap-x-5 px-5 py-4 border-t border-[#EFECE4] transition-colors duration-150 hover:bg-[#FFFCEF]"
         style={{ gridTemplateColumns: "210px repeat(4, minmax(0,1fr)) 260px 70px" }}
         data-testid={`overview-ledger-row-${a.key}`}>
      <div className="flex gap-3 min-w-0">
        <div className="w-[4px] rounded-full shrink-0" style={{ background: tone.edge }} />
        <div className="min-w-0">
          <div className="text-[15px] font-bold text-[#1A1A1A] truncate" style={MANROPE}>{a.name}</div>
          <div className="text-[12px] text-[#8A857C] truncate">{a.owner}</div>
        </div>
      </div>

      <div className="min-w-0">
        <div className="text-[26px] font-extrabold leading-none whitespace-nowrap"
             style={{ ...MANROPE, color: a.error ? "#8A857C" : tone.ink }}>
          {a.error || a.headline.value}
        </div>
        <div className="mt-1.5 text-[11.5px] text-[#59554D]">{a.error ? "Try again shortly" : a.headline.label}</div>
      </div>

      {a.measures.map((m, i) => (
        <div key={i} className="min-w-0">
          <div className="mono text-[15px] font-bold whitespace-nowrap" style={{ color: toneOf(m.tone).ink }}>{m.value}</div>
          <div className="mt-1.5 text-[11.5px] text-[#8A857C] leading-[1.3]">{m.label}</div>
        </div>
      ))}

      <Watch items={a.watch.items} empty="Nothing needs attention" />

      <button onClick={() => onOpen(a)} data-testid={`overview-open-${a.key}`}
              className="justify-self-end inline-flex items-center gap-1 text-[12.5px] font-semibold text-[#8A6D00] hover:text-[#6B5600] hover:underline">
        Open <ArrowRight className="w-3 h-3" />
      </button>
    </div>
  );
};

export const LedgerView = ({ data, onOpen }) => (
  <div className="space-y-4" data-testid="overview-ledger">
    <div className="rounded-[10px] overflow-hidden bg-[#121212]"
         style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))" }}
         data-testid="overview-band">
      {data.band.map((t, i) => <Tile key={t.label} t={t} first={i === 0} />)}
    </div>

    <div className="bg-white border border-[#DAD5CA] rounded-[10px] overflow-hidden">
      <div className="overflow-x-auto">
        <div style={{ minWidth: 1080 }}>
          <div className="grid gap-x-5 px-5 py-2.5 bg-[#FBFAF7]"
               style={{ gridTemplateColumns: "210px repeat(4, minmax(0,1fr)) 260px 70px" }}>
            <div className={LABEL}>Area · Owner</div>
            <div className={LABEL}>Headline</div>
            <div className={`${LABEL} whitespace-nowrap col-span-3`}>Key measures</div>
            <div className={LABEL}>Needs attention</div>
            <div />
          </div>
          {data.areas.map((a) => <Row key={a.key} a={a} onOpen={onOpen} />)}
        </div>
      </div>
    </div>
  </div>
);
